<script lang="ts">
  /**
   * StateBox — labeled key/value display for algorithm state.
   *
   * Used to show variables like `result =` or `power =` updating as steps advance.
   * The value crossfades + briefly highlights when it changes.
   */
  type Props = {
    label: string;
    value: string | number | bigint;
    /** Optional emphasized color when the value changes. */
    accent?: string;
  };

  let { label, value, accent = 'var(--accent)' }: Props = $props();

  const display = $derived(typeof value === 'bigint' ? value.toString() : String(value));
</script>

<div class="state-box" style="--accent: {accent};">
  <div class="state-label">{label}</div>
  {#key display}
    <div class="state-value">{display}</div>
  {/key}
</div>

<style>
  .state-box {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
    padding: var(--space-md) var(--space-lg);
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
    min-width: 140px;
  }

  .state-label {
    font-family: var(--font-sans);
    font-size: var(--text-xs);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--ink-muted);
    font-weight: var(--weight-medium);
  }

  .state-value {
    font-family: var(--font-mono);
    font-size: var(--text-lg);
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    word-break: break-all;
    animation: pulse-in 320ms ease;
  }

  @keyframes pulse-in {
    0% {
      color: var(--accent);
      transform: translateY(-1px);
    }
    100% {
      color: var(--ink);
      transform: translateY(0);
    }
  }
</style>
